// The estimating database pages that sit under "Database" in the drawer,
// next to Pricing Catalogs. Every page reads and writes the same curlight
// /data collections the desktop estimating app's database editor uses
// (BidlightApp/Views/Estimation/Database/*), so no data moves.
//
// Simple list pages are driven entirely by the field lists below; Labor,
// Tools, Materials and Catalog Mapping have their own pages.

import { createApiUrlIdentifier } from "helpers/estimatingDatabase/databaseRows";
import { mappedMetricUnit, unitConversionMultiplier } from "helpers/estimatingDatabase/databaseUnits";

// MaterialClassDatabaseViewModel.UpdateMeasurementType(Metric): the class
// unit mapped to its metric division unit (SF -> SM ...).
export const materialClassToMetric = (rec) => {
  const unit = mappedMetricUnit(rec.Unit);
  return unit === rec.Unit ? null : { ...rec, Unit: unit };
};

// ImperialMetricConversionVisitor.Visit(MaterialItemViewModel): Unit mapped
// to metric and UnitCost multiplied by
// UnitsProvider.GetUnitConversionMultiplier (no rounding).
export const materialToMetric = (rec) => {
  const from = rec.Unit || "SF";
  const unit = mappedMetricUnit(from);
  if (unit === from) return null;
  const cost = Number(rec.UnitCost) || 0;
  return { ...rec, Unit: unit, UnitCost: cost * unitConversionMultiplier(from, unit) };
};

export const TIME_UNITS = ["Hour", "Day", "Week", "Month", "Year"];
export const IMPERIAL_UNITS = ["SF", "LF", "CY", "SY", "TON", "LB", "EA"];
export const METRIC_UNITS = ["SM", "LM", "CM", "TONNE", "KG", "EA"];

const identity = (x) => x;
const num = (v) => (v === null || v === undefined || v === "" ? 0 : Number(v));

// Pick only the listed keys (plus _id / isPublic / fallback) off a record.
const pick = (keys) => (rec) => {
  const out = {};
  ["_id", "isPublic", "fallback", ...keys].forEach((k) => {
    if (rec[k] !== undefined) out[k] = rec[k];
  });
  return out;
};

const numberFields = (keys) => (rec) => {
  const out = { ...rec };
  keys.forEach((k) => {
    out[k] = num(rec[k]);
  });
  return out;
};

export const SIMPLE_PAGES = {
  "general-conditions": {
    title: "General Conditions",
    endpoint: "general_conditions",
    sheetName: "General Conditions",
    searchFields: ["Code", "Description"],
    keyField: "Description",
    fields: [
      { key: "Description", label: "Description", type: "text", required: true },
      { key: "Unit", label: "Unit", type: "select", options: "time" },
      { key: "UnitCost", label: "Unit Cost", type: "number", currency: true, min: 0 },
      { key: "Amount", label: "Amount", type: "number", min: 0 },
      { key: "Multiplier", label: "Multiplier", type: "number" },
    ],
    fromWire: numberFields(["UnitCost", "Amount", "Multiplier"]),
    toWire: pick(["Code", "Description", "Unit", "UnitCost", "Amount", "Multiplier"]),
  },
  "insurance-contingency": {
    title: "Insurance & Contingency",
    endpoint: "insurance_contingencies",
    sheetName: "Insurance Contingency",
    searchFields: ["Code", "Description"],
    keyField: "Description",
    fields: [
      { key: "Description", label: "Description", type: "text", required: true },
      { key: "Multiplier", label: "Multiplier", type: "number" },
    ],
    fromWire: numberFields(["Multiplier"]),
    toWire: pick(["Code", "Description", "Multiplier"]),
  },
  "project-requirements": {
    title: "Project Requirements",
    endpoint: "project_requirements",
    sheetName: "Project Requirements",
    searchFields: ["Code", "Description"],
    keyField: "Description",
    // ProjectRequirementResponseModel maps Amount to the "Quantity" field.
    fields: [
      { key: "Description", label: "Description", type: "text", required: true },
      { key: "Unit", label: "Unit", type: "select", options: "unitsAndTime" },
      { key: "UnitCost", label: "Unit Cost", type: "number", currency: true, min: 0 },
      { key: "Quantity", label: "Quantity", type: "number", min: 0 },
    ],
    fromWire: numberFields(["UnitCost", "Quantity"]),
    toWire: pick(["Code", "Description", "Unit", "UnitCost", "Quantity"]),
  },
  "special-allowances": {
    title: "Special Allowances",
    endpoint: "special_allowances",
    sheetName: "Special Allowances",
    searchFields: ["Code", "Description"],
    keyField: "Description",
    // SpecialAllowanceResponseModel maps Amount to the "Cost" field.
    fields: [
      { key: "Description", label: "Description", type: "text", required: true },
      { key: "Cost", label: "Cost", type: "number", currency: true, min: 0 },
    ],
    fromWire: numberFields(["Cost"]),
    toWire: pick(["Code", "Description", "Cost"]),
  },
  "survey-layout": {
    title: "Survey & Layout",
    endpoint: "survey_layouts",
    sheetName: "Survey Layout",
    searchFields: ["Code", "Description"],
    keyField: "Description",
    fields: [
      { key: "Description", label: "Description", type: "text", required: true },
      { key: "Unit", label: "Unit", type: "select", options: "time" },
      { key: "UnitCost", label: "Unit Cost", type: "number", currency: true, min: 0 },
      { key: "Amount", label: "Amount", type: "number", min: 0 },
    ],
    fromWire: numberFields(["UnitCost", "Amount"]),
    toWire: pick(["Code", "Description", "Unit", "UnitCost", "Amount"]),
  },
  clarifications: {
    title: "Clarifications",
    endpoint: "clarifications",
    sheetName: "Clarifications",
    searchFields: ["Code", "Description"],
    keyField: "Description",
    // The subject list is the clarification_types collection; see the
    // second tab below.
    fields: [
      { key: "Group", label: "Subject", type: "select", options: "clarificationTypes", required: true },
      { key: "Description", label: "Description", type: "textarea", required: true },
    ],
    // ClarificationsDatabaseViewModel: the server keeps Group as
    // "<subject>.<code>" on updates; the page works with the subject alone.
    fromWire: (w) => ({ ...w, Group: String(w.Group || "").split(".")[0] }),
    toWire: (rec) => pick(["Code", "Group", "Description"])({
      ...rec,
      Description: rec.Description ? String(rec.Description).trim() : rec.Description,
    }),
    // On PUT the desktop sends Group as "<subject>.<code>"; on POST only the
    // subject (CompanyToNewRequest vs CompanyToResponse).
    toUpdateWire: (rec) => ({
      ...pick(["Code", "Group", "Description"])(rec),
      Group: rec.Code ? `${rec.Group}.${rec.Code}` : rec.Group,
      Description: rec.Description ? String(rec.Description).trim() : rec.Description,
    }),
    secondary: {
      tab: "Subjects",
      title: "Subject",
      sheetName: "Clarification Subjects",
      endpoint: "clarification_types",
      searchFields: ["Code", "Description"],
      keyField: "Code",
      fields: [
        { key: "Code", label: "Code", type: "text", required: true, unique: true },
        { key: "Description", label: "Description", type: "text", required: true },
        { key: "Group", label: "Group", type: "text" },
      ],
      fromWire: identity,
      toWire: (rec) => pick(["Code", "Description", "Group"])({ ...rec, Group: rec.Group || rec.Code }),
    },
  },
  awp: {
    title: "Advanced Work Packages",
    endpoint: "awps",
    // BaseDatabaseModel.GenericForAllCompany: changes count without ticks.
    genericForAllCompany: true,
    sheetName: "AWP",
    searchFields: ["Awp", "Description"],
    keyField: "Awp",
    fields: [
      { key: "Awp", label: "AWP", type: "text", required: true, unique: true },
      { key: "Description", label: "Description", type: "text", required: true, unique: true },
    ],
    fromWire: identity,
    toWire: pick(["Code", "Awp", "Description"]),
    secondary: {
      tab: "Material assignments",
      title: "Material assignment",
      sheetName: "Material AWP",
      endpoint: "material_awp_maps",
      searchFields: ["Code", "Awp"],
      keyField: "Code",
      fields: [
        { key: "Code", label: "Material", type: "text", required: true },
        { key: "Awp", label: "AWP", type: "select", options: "awps", required: true },
      ],
      fromWire: identity,
      toWire: pick(["Code", "Awp"]),
    },
  },
  "project-types": {
    title: "Project Types",
    endpoint: "project_types",
    genericForAllCompany: true,
    sheetName: "Project Types",
    searchFields: ["Category"],
    keyField: "Category",
    fields: [
      { key: "Category", label: "Project Type", type: "text", required: true, unique: true },
      { key: "APIUrlIdentifier", label: "Identifier", type: "readonly" },
    ],
    fromWire: identity,
    toWire: (rec) =>
      pick(["Code", "Category", "APIUrlIdentifier"])({
        ...rec,
        APIUrlIdentifier: createApiUrlIdentifier(rec.Category),
      }),
  },
  "labor-types": {
    title: "Labor Types",
    endpoint: "labor_types",
    genericForAllCompany: true,
    sheetName: "Labor Types",
    searchFields: ["Category"],
    keyField: "Category",
    fields: [
      { key: "Category", label: "Labor Type", type: "text", required: true, unique: true },
      { key: "APIUrlIdentifier", label: "Identifier", type: "readonly" },
    ],
    fromWire: identity,
    toWire: (rec) =>
      pick(["Code", "Category", "APIUrlIdentifier"])({
        ...rec,
        APIUrlIdentifier: createApiUrlIdentifier(rec.Category),
      }),
  },
  "material-classes": {
    title: "Material Classes",
    endpoint: "material_classes",
    genericForAllCompany: true,
    metricOnSave: materialClassToMetric,
    sheetName: "Material Classes",
    searchFields: ["Code"],
    keyField: "Code",
    fields: [
      { key: "Code", label: "Name", type: "text", required: true, unique: true },
      { key: "Unit", label: "Unit", type: "select", options: "units", required: true },
    ],
    fromWire: identity,
    toWire: pick(["Code", "Unit"]),
  },
};

export const MATERIAL_FIELDS = [
  { key: "Code", label: "Name", type: "text", required: true, unique: true },
  { key: "UnitCost", label: "Unit Cost", type: "number", currency: true, min: 0 },
  { key: "Unit", label: "Unit", type: "select", options: "units" },
  // ItemViewModel: [Required(ErrorMessage = "Material Class is required")]
  { key: "MaterialClass", label: "Material Class", type: "select", options: "materialClasses", required: true },
  { key: "QuantSF", label: "Quant / SF", type: "number" },
  { key: "QuantLF", label: "Quant / LF", type: "number" },
  { key: "QuantCY", label: "Quant / CY", type: "number" },
  { key: "QuantSY", label: "Quant / SY", type: "number" },
  { key: "QuantTON", label: "Quant / TON", type: "number" },
  { key: "QuantLB", label: "Quant / LB", type: "number" },
  { key: "QuantEA", label: "Quant / EA", type: "number" },
];

const QUANT_KEYS = ["QuantSF", "QuantLF", "QuantCY", "QuantSY", "QuantTON", "QuantLB", "QuantEA"];

export const MATERIAL_PAGE = {
  title: "Materials",
  endpoint: "materials",
  sheetName: "Materials",
  searchFields: ["Code"],
  keyField: "Code",
  fields: MATERIAL_FIELDS,
  // MaterialsDatabaseViewModel.ImportData matches names exactly (case kept).
  exactImportKey: true,
  metricOnSave: materialToMetric,
  // MaterialsDatabaseViewModel: Unit defaults to SF both ways.
  fromWire: (w) => ({ ...numberFields(["UnitCost", ...QUANT_KEYS])(w), Unit: w.Unit || "SF" }),
  toWire: (rec) =>
    pick(["Code", "Unit", "UnitCost", "MaterialClass", ...QUANT_KEYS])({ ...rec, Unit: rec.Unit || "SF" }),
};

export const FORMULA_PAGE = {
  title: "Formulas",
  endpoint: "formulas",
  sheetName: "Formulas",
  searchFields: ["Code"],
  keyField: "Code",
  fields: [
    { key: "Code", label: "Name", type: "text", required: true, unique: true },
    { key: "Formula", label: "Formula", type: "formula", required: true },
  ],
  fromWire: identity,
  toWire: pick(["Code", "Formula"]),
};

export const FORMULA_MAP_PAGE = {
  title: "Formula assignments",
  endpoint: "formula_maps",
  sheetName: "Formula assignments",
  searchFields: ["Code"],
  keyField: "Code",
  fields: [
    { key: "Code", label: "Catalog + material", type: "text", required: true },
    { key: "FormulaConfig", label: "Formula", type: "formulaConfig" },
  ],
  fromWire: identity,
  toWire: pick(["Code", "FormulaConfig", "MaterialHash"]),
};

// CatalogMapsService picks the collection from the version's protocol.
export const CATALOG_MAP_ENDPOINTS = {
  RevitUniFormat: "catalog_maps",
  UniFormat: "catalog_uniformat_maps",
  CSIUniFormat: "catalog_uniformat_maps",
  MasterFormat: "catalog_master_maps",
  OmniClass: "catalog_omni_maps",
  TeslaMasterFormat: "catalog_tesla_master_maps",
};

export const CATALOG_MAP_PAGE = {
  title: "Model mappings",
  sheetName: "Model mappings",
  searchFields: ["Code", "Division"],
  keyField: "Code",
  fields: [
    { key: "Code", label: "Model key (group + type + category)", type: "text", required: true },
    { key: "Division", label: "Catalog division", type: "text", required: true },
  ],
  fromWire: identity,
  toWire: pick(["Code", "Division"]),
};

export const CREW_FIELDS = ["Code", "Description", "Awp", "YieldSF", "YieldLF", "YieldCY", "YieldSY", "YieldTON", "YieldLB", "YieldEA"];
export const LABOR_FIELDS = ["Code", "Description", "Percentage", "UnitCost", "Crew"];
export const TOOL_GROUP_FIELDS = ["Code", "Group", "Category", "Type"];
export const TOOL_FIELDS = ["Code", "Description", "Model", "Group", "Unit", "UnitCost", "Amount", "Awp", "YieldSF", "YieldLF", "YieldCY", "YieldSY", "YieldTON", "YieldLB", "YieldEA"];

// Every page in the drawer, in the order the desktop lists them.
export const DATABASE_NAV = [
  { key: "materials", path: "/database/materials", title: "Materials & Formulas" },
  { key: "labor", path: "/database/labor", title: "Labor & Crews" },
  { key: "tools", path: "/database/tools", title: "Tools & Equipment" },
  { key: "catalog-mapping", path: "/database/catalog-mapping", title: "Catalog Mapping" },
  { key: "special-allowances", path: "/database/special-allowances", title: "Special Allowances" },
  { key: "survey-layout", path: "/database/survey-layout", title: "Survey & Layout" },
  { key: "project-requirements", path: "/database/project-requirements", title: "Project Requirements" },
  { key: "general-conditions", path: "/database/general-conditions", title: "General Conditions" },
  { key: "insurance-contingency", path: "/database/insurance-contingency", title: "Insurance & Contingency" },
  { key: "clarifications", path: "/database/clarifications", title: "Clarifications" },
  { key: "awp", path: "/database/awp", title: "Work Packages (AWP)" },
  { key: "labor-types", path: "/database/labor-types", title: "Labor Types" },
  { key: "project-types", path: "/database/project-types", title: "Project Types" },
  { key: "material-classes", path: "/database/material-classes", title: "Material Classes" },
];
